/** Lowercase, treat hyphens/slashes as word breaks, collapse whitespace — so "B-cell" matches "B cell". */
export function normalizeForFdaSearch(s: string): string {
  return s
    .toLowerCase()
    .replace(/[\u2010-\u2015\u2212\u00ad]/g, '-')
    .replace(/[-_/]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const m = a.length;
  const n = b.length;
  const prev = new Array<number>(n + 1);
  const curr = new Array<number>(n + 1);
  for (let j = 0; j <= n; j += 1) prev[j] = j;
  for (let i = 1; i <= m; i += 1) {
    curr[0] = i;
    const ca = a.charCodeAt(i - 1);
    for (let j = 1; j <= n; j += 1) {
      const cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= n; j += 1) prev[j] = curr[j];
  }
  return prev[n];
}

function maxTypoDistance(wordLen: number): number {
  if (wordLen <= 4) return 1;
  if (wordLen <= 12) return 2;
  return 3;
}

function tokenMatchesHaystack(hayNorm: string, hayWords: string[], token: string): boolean {
  if (!token.length) return true;
  if (token.length === 1) {
    return hayWords.some((w) => w === token);
  }
  if (hayNorm.includes(token)) return true;
  for (const w of hayWords) {
    if (!w.length) continue;
    if (w === token) return true;
    const minLen = Math.min(w.length, token.length);
    if (minLen >= 3 && (w.includes(token) || token.includes(w))) return true;
    if (Math.abs(w.length - token.length) > maxTypoDistance(Math.max(w.length, token.length))) continue;
    if (levenshtein(token, w) <= maxTypoDistance(Math.max(token.length, w.length))) return true;
  }
  return false;
}

/** Split query on whitespace; each token must match (AND). Fuzzy + normalized punctuation/spacing. */
export function rowMatchesFdaSearch(searchQueryRaw: string, fieldTexts: (string | undefined)[]): boolean {
  const q = normalizeForFdaSearch(searchQueryRaw);
  const tokens = q.split(/\s+/).filter(Boolean);
  if (!tokens.length) return true;
  return fieldTexts.some((raw) => {
    if (!raw) return false;
    const hayNorm = normalizeForFdaSearch(raw);
    if (!hayNorm) return false;
    const hayWords = hayNorm.match(/\w+/g) ?? [];
    return tokens.every((token) => tokenMatchesHaystack(hayNorm, hayWords, token));
  });
}

/** Comma-separated terms; each segment uses smart FDA-style matching (AND across segments). */
export function rowMatchesSmartSearch(searchQueryRaw: string, fieldTexts: (string | undefined)[]): boolean {
  const parts = searchQueryRaw
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (!parts.length) return true;
  return parts.every((part) => rowMatchesFdaSearch(part, fieldTexts));
}
